
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Collection } from "@/lib/api/types";
import { useEffect } from "react";
import { CustomModelSelector } from "./CustomModelSelector";

const collectionSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  custom_generator_model: z.string().optional(),
  custom_improver_model: z.string().optional(),
});

interface CollectionFormProps {
  editingCollection: Collection | null;
  isSubmitting: boolean;
  onSubmit: (values: z.infer<typeof collectionSchema>) => void;
}

export const CollectionForm = ({
  editingCollection,
  isSubmitting,
  onSubmit,
}: CollectionFormProps) => {
  const form = useForm<z.infer<typeof collectionSchema>>({
    resolver: zodResolver(collectionSchema),
    defaultValues: {
      name: "",
      description: "",
      custom_generator_model: undefined,
      custom_improver_model: undefined,
    },
  });

  useEffect(() => {
    if (editingCollection) {
      form.reset({
        name: editingCollection.name,
        description: editingCollection.description || "",
        custom_generator_model: (editingCollection as any).custom_generator_model || undefined,
        custom_improver_model: (editingCollection as any).custom_improver_model || undefined,
      });
    } else {
      form.reset({
        name: "",
        description: "",
        custom_generator_model: undefined,
        custom_improver_model: undefined,
      });
    }
  }, [editingCollection, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter collection name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Enter collection description (optional)" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <CustomModelSelector
          generatorModel={form.watch("custom_generator_model")}
          improverModel={form.watch("custom_improver_model")}
          onGeneratorModelChange={(model) => form.setValue("custom_generator_model", model)}
          onImproverModelChange={(model) => form.setValue("custom_improver_model", model)}
        />

        <Button type="submit" disabled={isSubmitting}>
          {editingCollection ? "Update" : "Create"}
        </Button>
      </form>
    </Form>
  );
};
